import dayjs from "dayjs";
import { Dispatch } from "react";
import { MyLocatorInventoryItem } from "./components/MyLocatorInventoryTable/MyLocatorInventoryTable";
import {
  appendInventoryItemsAction,
  MyLocatorInventoryActions,
  setAllInventoryItemsAction,
  setInventoryItemsAction,
  setIsAuthenticationErrorAction,
  setIsLoadingAction,
  setIsLoadingMoreAction,
  setIsTechnicalErrorAction,
  setTotalInventoryItemsAction,
} from "./reducers/actions";
import { MyLocatorInventoryState } from "./types";
import d2dApi from "../../../api/d2d/d2dApi";

const PAGE_SIZE = 5000;

export const fetchMyLocatorInventory = async (
  serviceUrl: string,
  state: MyLocatorInventoryState,
  accessToken: string,
  dispatch: Dispatch<MyLocatorInventoryActions>
): Promise<void> => {
  dispatch(setIsLoadingAction(true));
  dispatch(setIsTechnicalErrorAction(false));
  dispatch(setIsAuthenticationErrorAction(false));

  try {
    const firstResponse = await d2dApi.fetchMyLocatorInventory(
      serviceUrl,
      accessToken,
      state.accountId,
      PAGE_SIZE,
      null
    );

    const { totalCount } = firstResponse.value;
    const firstPageItems = firstResponse.value.data.map((item, index) => ({
      id: index + 1,
      line: index + 1,
      partNumber: item.partNumber,
      quantity: item.availableQty,
      dateTimeUpdated: item.invChangeDate,
    }));

    dispatch(setAllInventoryItemsAction(firstPageItems));
    dispatch(setTotalInventoryItemsAction(totalCount));
    dispatch(setIsLoadingAction(false));

    applyFiltersAndPagination(firstPageItems, state, dispatch);

    const { nextPageToken } = firstResponse.value;

    if (nextPageToken) {
      dispatch(setIsLoadingMoreAction(true));

      const fetchRemainingPages = async (
        token: string,
        items: MyLocatorInventoryItem[],
        index: number
      ): Promise<void> => {
        const response = await d2dApi.fetchMyLocatorInventory(
          serviceUrl,
          accessToken,
          state.accountId,
          PAGE_SIZE,
          token
        );

        const baseIndex = index;
        const newItems = response.value.data.map((item, itemIndex) => ({
          id: baseIndex + itemIndex + 1,
          line: baseIndex + itemIndex + 1,
          partNumber: item.partNumber,
          quantity: item.availableQty,
          dateTimeUpdated: item.invChangeDate,
        }));

        const updatedItems = [...items, ...newItems];
        const updatedIndex = index + newItems.length;

        dispatch(appendInventoryItemsAction(newItems));
        applyFiltersAndPagination(updatedItems, state, dispatch);

        if (response.value.nextPageToken) {
          await fetchRemainingPages(
            response.value.nextPageToken,
            updatedItems,
            updatedIndex
          );
        }
      };

      await fetchRemainingPages(
        nextPageToken,
        [...firstPageItems],
        firstPageItems.length
      );
      dispatch(setIsLoadingMoreAction(false));
    }
  } catch (error: any) {
    if (error?.statusCode === 401 || error?.statusCode === 403) {
      dispatch(setIsAuthenticationErrorAction(true));
    } else {
      dispatch(setIsTechnicalErrorAction(true));
    }

    dispatch(
      setInventoryItemsAction({
        inventoryItems: [],
        totalInventoryItems: 0,
      })
    );
  } finally {
    dispatch(setIsLoadingAction(false));
    dispatch(setIsLoadingMoreAction(false));
  }
};

const applyFiltersAndPagination = (
  allItems: MyLocatorInventoryItem[],
  state: MyLocatorInventoryState,
  dispatch: Dispatch<MyLocatorInventoryActions>
): void => {
  let filteredData = [...allItems];

  if (state.filters.partNumber) {
    const searchTerm = state.filters.partNumber.toLowerCase();
    filteredData = filteredData.filter((item) =>
      item.partNumber.toLowerCase().includes(searchTerm)
    );
  }

  if (state.filters.quantity) {
    filteredData = filteredData.filter((item) =>
      item.quantity.toString().includes(state.filters.quantity!)
    );
  }

  if (state.filters.dateTimeUpdatedFrom || state.filters.dateTimeUpdatedTo) {
    filteredData = filteredData.filter((item) => {
      const itemDate = dayjs(item.dateTimeUpdated);
      let isInRange = true;

      if (state.filters.dateTimeUpdatedFrom) {
        isInRange =
          isInRange && itemDate.isAfter(state.filters.dateTimeUpdatedFrom);
      }

      if (state.filters.dateTimeUpdatedTo) {
        isInRange =
          isInRange &&
          itemDate.isBefore(state.filters.dateTimeUpdatedTo.endOf("day"));
      }

      return isInRange;
    });
  }

  if (state.sortModel.length > 0) {
    const { field, sort } = state.sortModel[0];
    filteredData.sort((a, b) => {
      const aValue = a[field as keyof typeof a];
      const bValue = b[field as keyof typeof b];

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sort === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sort === "asc" ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  }

  const startIndex = (state.currentPage - 1) * state.pageRowSize;
  const endIndex = startIndex + state.pageRowSize;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  dispatch(
    setInventoryItemsAction({
      inventoryItems: paginatedData,
      totalInventoryItems: filteredData.length,
    })
  );
};
